import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { SEOTags } from '@/components/SEOTags'

export default function PrivacyPage() {
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const breadcrumbSchema = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://steadfasthsa.com" },
            { "@type": "ListItem", "position": 2, "name": "Privacy Policy", "item": "https://steadfasthsa.com/privacy" }
        ]
    });

    return (
        <div className="min-h-screen bg-slate-50 font-sans selection:bg-teal-100 selection:text-teal-900 flex flex-col">
            <SEOTags
                title="Privacy Policy | Steadfast HSA"
                description="Read the Privacy Policy for Steadfast HSA. Understand how we handle, protect, and secure your personal and financial data, including HIPAA compliance aspects."
                url="https://steadfasthsa.com/privacy"
                schema={breadcrumbSchema}
            />
            {/* Header */}
            <nav className="bg-white border-b border-gray-200 py-4 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
                    <Link to="/" className="text-2xl font-bold tracking-tight text-navy">
                        STEADFAST <span className="text-gold">HSA</span>
                    </Link>
                    <Link
                        to="/"
                        className="text-sm font-semibold text-slate-600 hover:text-navy transition"
                    >
                        ← Back to Home
                    </Link>
                </div>
            </nav>

            {/* Content */}
            <main className="max-w-4xl mx-auto px-6 py-16">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-extrabold text-navy mb-2">Privacy Policy</h1>
                    <p className="text-slate-500">Effective Date: March 1, 2026</p>
                </div>

                <div className="prose prose-slate prose-lg max-w-none
          prose-headings:text-navy prose-headings:font-bold
          prose-h1:text-3xl prose-h1:mt-12 prose-h1:mb-6
          prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
          prose-a:text-blue-600 prose-a:underline
          prose-strong:text-navy
          prose-table:border-collapse prose-th:bg-gray-50 prose-th:p-3 prose-th:text-left prose-td:p-3 prose-td:border-t prose-td:border-gray-200
        ">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-10">
                        <p className="font-bold text-green-900 mb-2">OUR CORE PRIVACY COMMITMENTS</p>
                        <p className="text-green-800 text-sm leading-relaxed">
                            We do not sell your data. We do not use your data for advertising. Your data is yours. We store only what we need to provide the Service. You can export and delete your data at any time. We will never lock you in.
                        </p>
                    </div>

                    <h1 id="introduction">1. Introduction</h1>
                    <p>
                        Steadfast Financial, LLC ("Steadfast HSA," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform at <a href="https://steadfasthsa.com">https://steadfasthsa.com</a> (the "Service"). Please read this policy carefully. If you disagree with its terms, please do not use the Service.
                    </p>
                    <p>
                        The Service stores sensitive personal data, including health-adjacent information (medical expense receipts) and financial data (transaction information if you use the Plaid integration). We take the protection of this data seriously and apply heightened care to its handling.
                    </p>
                    <p>
                        This Policy applies to all users of the Service, including visitors to our website, registered account holders, and subscribers. It does not apply to third-party websites or services that may be linked from the Service.
                    </p>

                    <h1 id="info-collect">2. Information We Collect</h1>

                    <h2>2.1 Information You Provide Directly</h2>
                    <div className="overflow-x-auto">
                        <table>
                            <thead>
                                <tr>
                                    <th>Category of Data</th>
                                    <th>Examples</th>
                                    <th>Purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Account Information</strong></td>
                                    <td>Name, email address, password (hashed)</td>
                                    <td>Create and manage your account; authenticate your identity</td>
                                </tr>
                                <tr>
                                    <td><strong>Expense Records</strong></td>
                                    <td>Date of service, provider name, service description, amount paid, reimbursement status</td>
                                    <td>Core service functionality — organize and track HSA expenses</td>
                                </tr>
                                <tr>
                                    <td><strong>Receipt Files</strong></td>
                                    <td>Uploaded receipt images, PDFs, and other documents</td>
                                    <td>Receipt storage and documentation; export functionality</td>
                                </tr>
                                <tr>
                                    <td><strong>Family Member Data</strong></td>
                                    <td>First name or identifier for family members (self, spouse, dependents)</td>
                                    <td>Tag expenses by family member for organization</td>
                                </tr>
                                <tr>
                                    <td><strong>Multi-Account Data</strong></td>
                                    <td>HSA account names, custodian names (no account numbers collected)</td>
                                    <td>Track expenses across multiple HSA accounts</td>
                                </tr>
                                <tr>
                                    <td><strong>Payment Information</strong></td>
                                    <td>Billing address, payment card information (processed by Stripe; we do not store card numbers)</td>
                                    <td>Process subscription payments</td>
                                </tr>
                                <tr>
                                    <td><strong>Communications</strong></td>
                                    <td>Emails or messages you send to our support team</td>
                                    <td>Respond to your inquiries and improve the Service</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h2>2.2 Information Collected Automatically</h2>
                    <div className="overflow-x-auto">
                        <table>
                            <thead>
                                <tr>
                                    <th>Category of Data</th>
                                    <th>Examples</th>
                                    <th>Purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Usage Data</strong></td>
                                    <td>Pages visited, features used, timestamps, clicks, session duration</td>
                                    <td>Improve the Service; understand usage patterns; analytics</td>
                                </tr>
                                <tr>
                                    <td><strong>Technical Data</strong></td>
                                    <td>IP address, browser type and version, device type, operating system</td>
                                    <td>Security monitoring; troubleshooting; fraud prevention</td>
                                </tr>
                                <tr>
                                    <td><strong>Cookies &amp; Tracking</strong></td>
                                    <td>Session cookies, authentication tokens, preference cookies</td>
                                    <td>Maintain your login session; remember your preferences</td>
                                </tr>
                                <tr>
                                    <td><strong>Email Receipts</strong></td>
                                    <td>Content of emails forwarded to your Steadfast HSA receipt inbox</td>
                                    <td>Parse and create expense records from emailed receipts (Core/Pro only)</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h2>2.3 Information from Third-Party Integrations</h2>
                    <p>If you choose to use the following optional integrations, we collect additional data:</p>
                    <ul>
                        <li><strong>BYOS Cloud Storage (Pro Plan only):</strong> When you connect a cloud storage service (Google Drive, Dropbox, OneDrive, or iCloud), we receive an access token to write your exported data to a designated folder. We do not read files from your cloud storage that you did not create through Steadfast HSA.</li>
                    </ul>

                    <h1 id="how-use">3. How We Use Your Information</h1>
                    <p>We use the information we collect for the following purposes:</p>
                    <ul>
                        <li><strong>Service Delivery:</strong> Process, store, and organize your expense records; provide receipt storage, data export, and all other Service features.</li>
                        <li><strong>Account Management:</strong> Create and manage your account, verify your identity, and process subscription payments.</li>
                        <li><strong>Service Improvement:</strong> Analyze aggregate, anonymized usage patterns to improve features, fix bugs, and enhance the user experience.</li>
                        <li><strong>Communications:</strong> Send you transactional emails (receipts, password resets, account notices), product updates, and service announcements. See Section 11 for marketing communications.</li>
                        <li><strong>Security and Fraud Prevention:</strong> Detect and prevent unauthorized access, abuse, or fraud.</li>
                        <li><strong>Legal Compliance:</strong> Comply with applicable laws, respond to legal process, enforce our Terms of Service, and protect the rights of users and third parties.</li>
                    </ul>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 my-8">
                        <p className="font-bold text-blue-900 mb-2">WE DO NOT USE YOUR DATA FOR ADVERTISING</p>
                        <p className="text-blue-800 text-sm leading-relaxed">
                            We do not use your expense records, receipt content, or any health-adjacent data for advertising purposes. We do not build advertising profiles. We do not sell or license your data to advertising networks or data brokers. Our business model is subscription revenue from users, not data monetization.
                        </p>
                    </div>

                    <h1 id="how-share">4. How We Share Your Information</h1>

                    <h2>4.1 Service Providers (Sub-processors)</h2>
                    <p>
                        We share your information with carefully selected third-party service providers who process data on our behalf to provide the Service. All sub-processors are contractually obligated to use your data only for the purposes we specify and to implement appropriate security measures.
                    </p>
                    <div className="overflow-x-auto">
                        <table>
                            <thead>
                                <tr>
                                    <th>Provider</th>
                                    <th>Data Shared</th>
                                    <th>Purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Clerk</strong></td>
                                    <td>Account credentials, authentication tokens, user identity</td>
                                    <td>Authentication and user identity management</td>
                                </tr>
                                <tr>
                                    <td><strong>Cloudflare R2</strong></td>
                                    <td>Receipt images, PDF files, exported ZIP archives</td>
                                    <td>Encrypted file storage</td>
                                </tr>
                                <tr>
                                    <td><strong>Neon (PostgreSQL)</strong></td>
                                    <td>Expense records, account metadata, application data</td>
                                    <td>Database hosting and storage</td>
                                </tr>
                                <tr>
                                    <td><strong>Railway</strong></td>
                                    <td>Application traffic, API requests</td>
                                    <td>Backend server hosting</td>
                                </tr>
                                <tr>
                                    <td><strong>Stripe</strong></td>
                                    <td>Payment card information, billing address, transaction history</td>
                                    <td>Payment processing (PCI-DSS compliant)</td>
                                </tr>
                                <tr>
                                    <td><strong>Plaid</strong></td>
                                    <td>Financial account authorization, transaction data (if enabled)</td>
                                    <td>Financial account integration (Pro Plan only)</td>
                                </tr>
                                <tr>
                                    <td><strong>PostHog</strong></td>
                                    <td>Anonymized usage events, feature interactions (no receipt content)</td>
                                    <td>Product analytics and user experience improvement</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h2>4.2 Legal Requirements</h2>
                    <p>
                        We may disclose your information if required by law, court order, subpoena, or other legal process, or if we believe in good faith that such disclosure is necessary to: (a) comply with applicable law; (b) protect our legal rights; (c) respond to an emergency that threatens the safety of any person; or (d) investigate potential violations of our Terms of Service.
                    </p>

                    <h2>4.3 Business Transfers</h2>
                    <p>
                        If Steadfast HSA is involved in a merger, acquisition, or sale of substantially all of its assets, your information may be transferred as part of that transaction. We will provide notice of any such transfer and any changes to this Privacy Policy. Any acquirer will be required to honor the terms of this Privacy Policy for a minimum of 12 months following the transfer.
                    </p>

                    <h2>4.4 We Do Not Sell or Share Your Data</h2>
                    <p>
                        We do not sell, rent, trade, or otherwise share your personal information or health-adjacent data with third parties for their own marketing or commercial purposes. We do not participate in data broker exchanges. This commitment applies regardless of our subscriber count or the requirements of state privacy laws.
                    </p>

                    <h1 id="health-data">5. Health-Adjacent Data and the FTC Health Breach Notification Rule</h1>
                    <p>
                        The expense records and receipt images you store in Steadfast HSA may include health-adjacent information, such as the names of medical providers, dates of service, types of medical services, and information about prescription medications. While Steadfast HSA is a documentation tool and not a healthcare provider, we recognize the sensitivity of this data and apply heightened protections.
                    </p>
                    <p>
                        We believe the FTC Health Breach Notification Rule (16 CFR Part 318) applies to Steadfast HSA as a personal health record vendor. In the event of a breach of your unsecured health-adjacent data, we will:
                    </p>
                    <ul>
                        <li>Notify you within 60 days of discovering the breach, as required by the Rule.</li>
                        <li>Notify the Federal Trade Commission as required.</li>
                        <li>For breaches affecting 500 or more users in a state, notify prominent media outlets as required.</li>
                        <li>Provide you with a description of the breach, the categories of data affected, and the steps we are taking to investigate and remediate.</li>
                    </ul>
                    <p>
                        We will maintain a breach response plan and conduct regular testing of our incident response procedures. If you suspect unauthorized access to your account or data, please contact us immediately at <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a>.
                    </p>

                    <h1 id="retention">6. Data Retention</h1>
                    <p>
                        We retain your data for as long as your account is active or as needed to provide the Service. We are designed for long-term data retention — that is our core value proposition — and we will not delete your data without your explicit instruction or in the circumstances described below.
                    </p>
                    <ul>
                        <li><strong>Active Accounts:</strong> Your data is retained indefinitely while your account remains active, including Free Tier accounts. We believe your records belong to you and should persist as long as you need them.</li>
                        <li><strong>After Account Deletion:</strong> When you delete your account, we retain your data for 30 days to allow for recovery in case of accidental deletion. After 30 days, all personal data is permanently deleted from our systems and backups. We will confirm deletion upon request.</li>
                        <li><strong>Subscription Lapse:</strong> If a paid subscription lapses and your account is downgraded to Free Tier, your data is retained in full for 90 days. If the account is not reactivated, data exceeding Free Tier limits may be deleted after 90 days with prior email notice.</li>
                        <li><strong>Legal Hold:</strong> We may retain certain data longer if required by law or as part of a legal proceeding.</li>
                        <li><strong>Backup Retention:</strong> System backups are retained for up to 30 days, after which they are permanently deleted. Data deleted from your account will be removed from backups within this window.</li>
                    </ul>

                    <h1 id="data-security">7. Data Security</h1>
                    <p>We implement commercially reasonable technical and organizational security measures to protect your data, including:</p>
                    <ul>
                        <li><strong>Encryption in Transit:</strong> All data transmitted between your browser and our servers is encrypted using TLS 1.2 or higher.</li>
                        <li><strong>Encryption at Rest:</strong> All expense records and receipt files are encrypted at rest in our database and file storage systems.</li>
                        <li><strong>Access Controls:</strong> Strict role-based access controls limit which personnel can access production data. Developer access to production user data is logged and audited.</li>
                        <li><strong>Authentication Security:</strong> We support multi-factor authentication (MFA) for all user accounts and strongly encourage its use.</li>
                        <li><strong>Third-Party Security:</strong> Our cloud infrastructure providers (Cloudflare, Neon) maintain SOC 2 certifications and implement industry-standard security practices.</li>
                    </ul>
                    <p>
                        Despite these measures, no security system is impenetrable. We cannot guarantee that your data will never be accessed, disclosed, altered, or destroyed by unauthorized parties. You should use strong, unique passwords and enable MFA to protect your account.
                    </p>

                    <h1 id="privacy-rights">8. Your Privacy Rights</h1>

                    <h2>8.1 Rights Available to All Users</h2>
                    <p>Regardless of where you are located, you have the following rights with respect to your personal data:</p>
                    <ul>
                        <li><strong>Right to Access:</strong> You may request a copy of all personal data we hold about you by contacting us at <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a> or using the in-app data export feature.</li>
                        <li><strong>Right to Portability:</strong> You may export your complete expense history (CSV) and all receipt files (ZIP archive) at any time using the built-in export feature, at no charge and without limitation.</li>
                        <li><strong>Right to Correction:</strong> You may correct inaccurate personal data directly in the Service or by contacting us.</li>
                        <li><strong>Right to Deletion:</strong> You may request deletion of your account and all associated data through your Account Settings or by contacting us. See Section 6 for retention timelines.</li>
                        <li><strong>Right to Withdraw Consent:</strong> Where processing is based on your consent (e.g., Plaid integration, marketing emails), you may withdraw consent at any time through your Account Settings.</li>
                    </ul>

                    <h2>8.2 California Residents (CCPA/CPRA)</h2>
                    <p>
                        If you are a California resident, the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA), provides you with the following additional rights:
                    </p>
                    <ul>
                        <li><strong>Right to Know:</strong> You have the right to know what categories of personal information we collect, the purposes for which we use it, and whether we share it with third parties.</li>
                        <li><strong>Right to Delete:</strong> You have the right to request deletion of personal information we have collected from you, subject to certain exceptions.</li>
                        <li><strong>Right to Correct:</strong> You have the right to correct inaccurate personal information.</li>
                        <li><strong>Right to Opt Out of Sale or Sharing:</strong> We do not sell or share personal information for advertising purposes, so there is nothing to opt out of. However, you may submit a request to verify this at <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a>.</li>
                        <li><strong>Right to Limit Use of Sensitive Personal Information:</strong> You have the right to direct us to limit the use of sensitive personal information to only what is necessary to provide the Service.</li>
                        <li><strong>Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights.</li>
                    </ul>
                    <p>To exercise your California rights, submit a verifiable consumer request to <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a>. We will respond within 45 days.</p>

                    <h2>8.3 Washington State Residents (My Health MY Data Act)</h2>
                    <p>
                        Washington's My Health MY Data Act (MHMDA) provides additional protections for consumer health data. As a platform that processes health-adjacent data, we comply with MHMDA's requirements:
                    </p>
                    <ul>
                        <li>We do not collect, process, share, or sell consumer health data beyond what is necessary to provide the Service.</li>
                        <li>We do not use geofencing or location tracking in connection with health data.</li>
                        <li>Washington residents may request to confirm whether we process their health data, obtain a copy, request deletion, and withdraw consent to processing.</li>
                    </ul>
                    <p>To exercise your MHMDA rights, contact us at <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a>.</p>

                    <h2>8.4 Submitting Privacy Requests</h2>
                    <p>To exercise any of the rights described above, you may:</p>
                    <ul>
                        <li>Use the in-app tools in Account Settings (fastest method for most requests).</li>
                        <li>Email us at <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a> with the subject line "Privacy Request."</li>
                    </ul>
                    <p>
                        We may need to verify your identity before processing your request. We will respond to verified requests within 45 days. If we need more time, we will notify you within the initial 45-day period. We will not charge a fee for reasonable requests.
                    </p>

                    <h1 id="cookies">9. Cookies and Tracking Technologies</h1>
                    <p>We use cookies and similar tracking technologies to operate the Service. Cookies are small text files placed on your device when you visit our website.</p>

                    <h2>9.1 Types of Cookies We Use</h2>
                    <ul>
                        <li><strong>Strictly Necessary Cookies:</strong> Required for the Service to function, including session management and authentication. These cannot be disabled.</li>
                        <li><strong>Functional Cookies:</strong> Remember your preferences (e.g., date format, display settings). Can be disabled, but some features may not work properly.</li>
                        <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Service (via PostHog). We configure analytics to avoid capturing the content of your expense records or receipt files. Can be opted out.</li>
                    </ul>
                    <p>We do not use advertising cookies or tracking pixels for third-party advertising.</p>

                    <h2>9.2 Cookie Consent and Control</h2>
                    <p>
                        When you first visit the Service, we will present a cookie consent banner where you can choose which non-essential cookies to accept. You can update your preferences at any time through the Cookie Settings option in the website footer. You can also control cookies through your browser settings, though disabling cookies may affect Service functionality.
                    </p>

                    <h2>9.3 Do Not Track</h2>
                    <p>
                        Some browsers include a "Do Not Track" (DNT) feature. We currently do not respond to DNT signals, as there is no consistent industry standard for compliance. We do honor your cookie preferences as set through our consent manager.
                    </p>

                    <h1 id="children">10. Children's Privacy</h1>
                    <p>
                        The Service is intended for adults (18 years of age or older) only. We do not knowingly collect personal information from children under 13. If you are under 18, do not use the Service.
                    </p>
                    <p>
                        The Service includes a "family member tracking" feature that allows you to associate expenses with your dependents (including minor children) for categorization purposes. This feature does not create accounts for minor children, and we do not collect any direct personal information from children — the information is entered by the adult account holder.
                    </p>
                    <p>
                        If we become aware that we have inadvertently collected personal information from a child under 13, we will promptly delete that information. If you believe a child has provided personal information to us, please contact us at <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a>.
                    </p>

                    <h1 id="communications">11. Communications and Email Marketing</h1>

                    <h2>11.1 Transactional Emails</h2>
                    <p>
                        We will send you transactional emails related to your account and the Service, including: account verification, password reset, payment receipts, subscription renewal reminders, data deletion warnings, and security alerts. You cannot opt out of transactional emails while your account is active.
                    </p>

                    <h2>11.2 Product and Marketing Emails</h2>
                    <p>
                        We may send you product updates, feature announcements, educational content about HSA strategy, and occasional promotional emails. You may opt out of marketing emails at any time by:
                    </p>
                    <ul>
                        <li>Clicking the "Unsubscribe" link in any marketing email.</li>
                        <li>Updating your email preferences in Account Settings.</li>
                        <li>Contacting us at <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a>.</li>
                    </ul>
                    <p>
                        We will honor unsubscribe requests within 10 business days. We will continue to send you transactional emails after you unsubscribe from marketing communications.
                    </p>

                    <h2>11.3 CAN-SPAM Compliance</h2>
                    <p>
                        All commercial email we send complies with the CAN-SPAM Act, including: accurate sender information, honest subject lines, physical mailing address in every email, and a functioning unsubscribe mechanism.
                    </p>

                    <h1 id="international">12. International Users</h1>
                    <p>
                        The Service is intended for use by residents of the United States only. Our servers and operations are located in the United States. By using the Service from outside the United States, you consent to the transfer of your data to the United States, where data protection laws may differ from those in your home country.
                    </p>
                    <p>
                        We do not knowingly accept users from the European Economic Area (EEA), United Kingdom, or Canada without ensuring compliance with applicable local law (GDPR, UK GDPR, or CASL). If you access the Service from these regions, please be aware that we are not currently configured for full GDPR or CASL compliance. We may implement appropriate safeguards in the future as we expand internationally.
                    </p>

                    <h1 id="third-party">13. Third-Party Links and Services</h1>
                    <p>
                        The Service may contain links to third-party websites, and our integrations (Plaid, BYOS providers) connect to third-party services. This Privacy Policy does not apply to those third-party sites or services. We encourage you to review the privacy policies of any third parties you interact with. We are not responsible for the privacy practices of third parties.
                    </p>

                    <h1 id="changes">14. Changes to This Privacy Policy</h1>
                    <p>
                        We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or for other operational reasons. If we make material changes, we will:
                    </p>
                    <ul>
                        <li>Post the updated Policy on our website with a new Effective Date.</li>
                        <li>Notify you by email at least 30 days before the changes take effect.</li>
                        <li>For changes involving how we handle health-adjacent data, we will also provide an in-app notice.</li>
                    </ul>
                    <p>
                        Your continued use of the Service after the effective date of the updated Policy constitutes your acceptance of the changes. If you disagree with any changes, you must stop using the Service and may request deletion of your data as described in Section 8.
                    </p>

                    <h1 id="contact">15. Contact Us</h1>
                    <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact our Privacy team:</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 my-8">
                        <p className="font-bold text-navy mb-2">Steadfast Financial, LLC</p>
                        <p className="text-slate-600 text-sm">
                            Privacy Inquiries: <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a><br />
                            Data Deletion Requests: <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a> (Subject: Privacy Request)<br />
                            Security Incidents: <a href="mailto:security@steadfasthsa.com">security@steadfasthsa.com</a><br />
                            Website: <a href="https://steadfasthsa.com">https://steadfasthsa.com</a>
                        </p>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-gray-200 text-center text-sm text-slate-400">
                    STEADFAST HSA &nbsp;|&nbsp; Privacy Policy &nbsp;|&nbsp; Effective March 1, 2026
                </div>
            </main>

            {/* Footer */}
            <footer className="py-12 bg-white border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row justify-between items-center text-sm text-slate-500">
                        <div className="mb-6 md:mb-0">
                            <span className="font-bold text-navy">STEADFAST HSA</span> © {new Date().getFullYear()}. Built for the long term.
                        </div>
                        <div className="flex flex-wrap justify-center gap-6 md:gap-8">
                            <Link to="/" className="hover:text-navy transition">Home</Link>
                            <Link to="/privacy" className="hover:text-navy transition font-semibold text-navy">Privacy Policy</Link>
                            <Link to="/terms" className="hover:text-navy transition">Terms of Service</Link>
                            <Link to="/security" className="hover:text-navy transition">Security</Link>
                        
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    )
}
